import React, { FC, ReactNode, useEffect, useState } from 'react';

import './RolloutValue.css';

import { LaunchUniversal } from '@duxton-web/icons';
import {
  Alert,
  ConfigProvider,
  Flex,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
} from '@duxton-web/ui';
import { debounce } from 'lodash';

import { validateValue } from '@/common/StaticValueInput/ajv';
import JsonEditorModal from '@/components/json-editor-modal';
import { VariableType } from '@/constants';
import { GenericValue, VariableInfo } from '@/lib/App';
import JsonEditor from '@/pages/ExperimentFormV2/common/JsonEditor';
import { prettifyJSONRaw } from '@/utils/json';

export interface RolloutValueProps {
  value: GenericValue;
  variable: VariableInfo;
  disabled?: boolean;
  onChange: (v: GenericValue) => any;
}

const RolloutValue: FC<RolloutValueProps> = ({
  value,
  variable,
  disabled,
  onChange,
}) => {
  const [rolloutValueForm] = Form.useForm();
  const [error, setError] = useState<string>('');
  const [showEditJsonModal, setShowEditJsonModal] = useState({
    show: false,
    variableName: null,
    value: '',
    jsonSchema: '',
  });

  useEffect(() => {
    setError(validateValue(variable, value));

    Object.keys(value).forEach((key: string) => {
      rolloutValueForm.setFieldValue(key, value?.[key]);
    });
  }, [value.value, value.string]);

  function onValueInputChange(v: GenericValue): void {
    onChange(v);
  }

  function renderFormItem(): ReactNode {
    const variableType = variable?.type as VariableType;

    switch (variableType) {
      case VariableType.BOOLEAN:
      case VariableType.FLOAT64:
      case VariableType.INT64:
        return (
          <Flex vertical>
            <Form.Item
              name="value"
              validateStatus={error ? 'error' : 'success'}
            >
              {renderControl()}
            </Form.Item>
            {error ? <Alert type="error" message={error} /> : false}
          </Flex>
        );
      case VariableType.STRING:
      case VariableType.FILE:
      case VariableType.JSON:
      case VariableType.MAP:
        return (
          <Flex vertical>
            <Form.Item
              name="string"
              validateStatus={error ? 'error' : 'success'}
            >
              {renderControl()}
            </Form.Item>
            {error ? <Alert type="error" message={error} /> : false}
          </Flex>
        );
      default:
        return false;
    }
  }

  function renderEditJsonModal(): ReactNode {
    if (!showEditJsonModal.show) return false;

    return (
      <JsonEditorModal
        readOnly={disabled}
        data={showEditJsonModal.value}
        jsonSchema={showEditJsonModal.jsonSchema}
        onCancel={() => {
          setShowEditJsonModal({
            show: false,
            variableName: null,
            value: '',
            jsonSchema: '',
          });
        }}
        onOk={(jsString) => {
          onValueInputChange({
            ...value,
            string: jsString,
          });
          setShowEditJsonModal({
            show: false,
            variableName: null,
            value: '',
            jsonSchema: '',
          });
        }}
      />
    );
  }

  function renderControl(): ReactNode {
    const variableType = variable?.type as VariableType;

    if (variable?.metadata?.jsonSchema) {
      const parsedJSON = JSON.parse(variable.metadata.jsonSchema) as any;

      if (parsedJSON?.enum) {
        const values = parsedJSON.enum;

        return (
          <Select
            disabled={disabled}
            options={values.map((val) => {
              return {
                value: val,
                label: val,
              };
            })}
            onChange={(val) => {
              switch (variableType) {
                case VariableType.FLOAT64:
                case VariableType.INT64:
                  onValueInputChange({
                    ...value,
                    value: val as number,
                  });
                  break;
                case VariableType.STRING:
                case VariableType.FILE:
                  onValueInputChange({
                    ...value,
                    string: val,
                  });
                  break;
              }
            }}
          />
        );
      }
    }

    switch (variableType) {
      case VariableType.BOOLEAN:
        return (
          <Segmented
            defaultValue={value.value}
            disabled={disabled}
            style={{
              alignSelf: 'self-start',
              fontSize: 14,
            }}
            options={[
              {
                label: 'False',
                value: 0,
              },
              {
                label: 'True',
                value: 1,
              },
            ]}
            onChange={(val) => {
              onValueInputChange({
                ...value,
                value: val,
              });
            }}
          />
        );
      case VariableType.FLOAT64:
      case VariableType.INT64:
        return (
          <InputNumber
            disabled={disabled}
            style={{
              alignSelf: 'self-start',
              width: 300,
            }}
            onChange={debounce((val) => {
              onValueInputChange({
                ...value,
                value: val as number,
              });
            }, 200)}
          />
        );
      case VariableType.STRING:
      case VariableType.FILE:
        return (
          <Input
            disabled={disabled}
            onChange={debounce((e) => {
              const val = e.target.value;

              onValueInputChange({
                ...value,
                string: val,
              });
            }, 200)}
          />
        );
      default: {
        const jsonString = value?.string || '';
        let prettifiedJSON: string = jsonString;

        try {
          prettifiedJSON = prettifyJSONRaw(jsonString);
        } catch (e) {
          console.error(e);
        }

        return (
          <>
            <JsonEditor
              data={prettifiedJSON}
              actionButtonIcon={<LaunchUniversal />}
              readOnly={disabled}
              onJsonChange={(val: string) => {
                onValueInputChange({
                  ...value,
                  string: val,
                });
              }}
              onClickActionButton={() => {
                setShowEditJsonModal({
                  show: true,
                  variableName: variable?.name,
                  value: prettifiedJSON,
                  jsonSchema: variable?.metadata?.jsonSchema,
                });
              }}
              wrapEnabled
              debounceChangePeriod={700}
            />
            {renderEditJsonModal()}
          </>
        );
      }
    }
  }

  function _renderValueDescription(): ReactNode {
    const variableType = variable?.type as VariableType;

    switch (variableType) {
      case VariableType.BOOLEAN:
      case VariableType.FLOAT64:
      case VariableType.INT64:
        return (
          <Form.Item name="description">
            <Input
              placeholder="What does this value mean? Eg. algorithem-x (Optional)"
              onChange={(e) => {
                onValueInputChange({
                  ...value,
                  string: e.target.value,
                });
              }}
            />
          </Form.Item>
        );
      default:
        return false;
    }
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 8,
          },
        },
      }}
    >
      <Form
        form={rolloutValueForm}
        style={{
          paddingTop: 8,
        }}
      >
        {renderFormItem()}
      </Form>
    </ConfigProvider>
  );
};

export default RolloutValue;
